import PageSEO from '../components/PageSEO';
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { User, Mail, Globe, MapPin, Lock, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    country: user?.country || '',
    city: user?.city || ''
  });

  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleProfileUpdate = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, profileData, { withCredentials: true });
      setUser({ ...user, ...profileData });
      setMessage({ type: 'success', text: 'Profil bilgileriniz güncellendi!' });
      setEditMode(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Profil güncelleme başarısız oldu.' });
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Şifreler eşleşmiyor!' });
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/profile/change-password`,
        { old_password: passwordData.old_password, new_password: passwordData.new_password },
        { withCredentials: true }
      );
      setMessage({ type: 'success', text: 'Şifreniz başarıyla değiştirildi!' });
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Şifre değiştirme başarısız oldu.' });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/profile`, { withCredentials: true });
      await logout();
      navigate('/');
    } catch (error) {
      setMessage({ type: 'error', text: 'Hesap silme başarısız oldu.' });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      <PageSEO title="Profil Ayarları" description="Hesap bilgilerinizi yönetin" />
      <Header />      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-white mb-2" data-testid="profile-title">
            Profil Ayarları
          </h1>
          <p className="text-base leading-relaxed text-white/60">
            Hesap bilgilerinizi yönetin ve güncelleyin
          </p>
        </div>

        {message.text && (
          <div className={`mb-6 rounded-xl p-4 flex items-center space-x-2 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`} data-testid="profile-message">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
            <p className={`text-sm ${
              message.type === 'success' ? 'text-green-700' : 'text-red-600'
            }`}>{message.text}</p>
          </div>
        )}

        {/* Profile Information */}
        <div className="glass-card-md p-6 sm:p-8 mb-6" data-testid="profile-info-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white/90">Profil Bilgileri</h2>
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="text-blue-500 hover:text-blue-600 font-medium transition-colors"
                data-testid="edit-profile-button"
              >
                Düzenle
              </button>
            )}
          </div>

          {editMode ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Ad</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Soyad</label>
                  <input
                    type="text"
                    value={profileData.surname}
                    onChange={(e) => setProfileData({ ...profileData, surname: e.target.value })}
                    className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                    data-testid="profile-surname-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Ülke</label>
                  <input
                    type="text"
                    value={profileData.country}
                    onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                    className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                    data-testid="profile-country-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Şehir</label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                    className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                    data-testid="profile-city-input"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleProfileUpdate}
                  className="bg-blue-500 hover:bg-cyan-600 text-white rounded-full px-6 py-3 font-medium transition-all duration-200 shadow-[0_4px_14px_0_rgb(14,165,233,0.39)] hover:shadow-[0_6px_20px_rgba(14,165,233,0.23)] hover:-translate-y-0.5"
                  data-testid="save-profile-button"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="border border-white/10 hover:border-white/20 text-white/70 rounded-full px-6 py-3 font-medium transition-colors duration-200 bg-white/5"
                  data-testid="cancel-edit-button"
                >
                  İptal
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-white/70">
                <User className="w-5 h-5 text-slate-400" />
                <span>{user?.name} {user?.surname}</span>
              </div>
              <div className="flex items-center space-x-3 text-white/70">
                <Mail className="w-5 h-5 text-slate-400" />
                <span>{user?.email}</span>
              </div>
              <div className="flex items-center space-x-3 text-white/70">
                <Globe className="w-5 h-5 text-slate-400" />
                <span>{user?.country}</span>
              </div>
              <div className="flex items-center space-x-3 text-white/70">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span>{user?.city}</span>
              </div>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="glass-card-md p-6 sm:p-8 mb-6" data-testid="change-password-card">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white/90 mb-6">Şifre Değiştir</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Mevcut Şifre</label>
              <input
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                data-testid="old-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Yeni Şifre</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Yeni Şifre Tekrar</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/40 outline-none transition-all duration-200 text-white"
                data-testid="confirm-password-input"
              />
            </div>
            <button
              onClick={handlePasswordChange}
              className="bg-blue-500 hover:bg-cyan-600 text-white rounded-full px-6 py-3 font-medium transition-all duration-200 shadow-[0_4px_14px_0_rgb(14,165,233,0.39)] hover:shadow-[0_6px_20px_rgba(14,165,233,0.23)] hover:-translate-y-0.5"
              data-testid="change-password-button"
            >
              Şifreyi Değiştir
            </button>
          </div>
        </div>

        {/* Delete Account */}
        <div className="bg-red-50 border border-red-100 rounded-3xl p-6 sm:p-8" data-testid="delete-account-card">
          <div className="flex items-start space-x-3 mb-4">
            <Trash2 className="w-6 h-6 text-red-500 mt-1" />
            <div>
              <h2 className="text-xl font-medium text-red-800 mb-1">Hesabı Sil</h2>
              <p className="text-sm text-red-700">Hesabınızı silerseniz tüm verileriniz kalıcı olarak silinecektir.</p>
            </div>
          </div>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-3 font-medium transition-all duration-200"
              data-testid="delete-account-button"
            >
              Hesabımı Sil
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-red-700 font-medium">Emin misiniz? Bu işlem geri alınamaz!</p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteAccount}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 py-3 font-medium transition-all duration-200"
                  data-testid="confirm-delete-button"
                >
                  Evet, Sil
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="border border-white/10 hover:border-white/20 text-white/70 rounded-full px-6 py-3 font-medium transition-colors duration-200 bg-white/5"
                  data-testid="cancel-delete-button"
                >
                  İptal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;